import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export const armApiRef = createApiRef<ArmApi>({
  id: 'plugin.kaap.armapi',
});

export type AdGroup = { id: string; name: string };

export type GetGroupsParams = {
  ids?: string[];
  match?: string;
  isMember?: boolean;
};

export interface ArmApi {
  getGroup(groupId: string): Promise<AdGroup>;
  getGroups(params: GetGroupsParams): Promise<AdGroup[]>;
}

// Azure AD groups that are not meaningful tenant security groups and should be
// filtered out of pickers and membership checks.
const WELL_KNOWN_GROUPS = [
  'dcedc4ca-ef7d-4e38-8f98-713505aa017b',
  'b2f412ba-da2f-4adf-a171-8439a18822e7',
  '5543ef50-dfb8-4209-9675-30633be0e91c',
  '62376fb8-8ecd-4b5d-9283-f8c90855ce12',
  '97ed1212-89a3-45b8-8ab0-795197fb3bb2',
  'cf7e0dca-ea6e-4851-a081-a0c9a4e82b2e',
  'f5e93fdd-1c6f-475d-bea2-2c24ed90a8a1',
];

const groupFilter = (group: AdGroup): boolean =>
  !WELL_KNOWN_GROUPS.includes(group.id.toLowerCase()) &&
  !group.name?.toUpperCase().startsWith('RG_AZ_PIM_') &&
  !group.name?.toUpperCase().startsWith('PG_CONCUR_SSO') &&
  !group.name?.toUpperCase().startsWith('RG_APP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_APP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_LOC_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_TT_') &&
  !group.name?.toUpperCase().startsWith('RG_VPN_') &&
  !group.name?.toUpperCase().startsWith('SPO_LOCATION_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_SLACK_USER_GROUP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_CLOUDSPEND_READERS');

export type ArmApiClientOptions = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
};

/**
 * Resolves Azure AD security groups through the common-utils backend, which
 * queries Microsoft Graph with its own application credentials.
 */
export class ArmApiClient implements ArmApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: ArmApiClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getGroup(groupId: string): Promise<AdGroup> {
    const [group] = await this.resolve([groupId]);
    if (!group) {
      throw new Error(`Failed to resolve AD group ${groupId}`);
    }
    return group;
  }

  async getGroups(params: GetGroupsParams): Promise<AdGroup[]> {
    let groups: AdGroup[];
    if (params.ids && params.ids.length > 0) {
      groups = await this.resolve(params.ids);
    } else if (params.isMember) {
      groups = await this.request('/security-groups/mine');
    } else {
      const query = new URLSearchParams({ search: params.match ?? '' });
      groups = await this.request(`/security-groups?${query.toString()}`);
    }
    return groups.filter(groupFilter);
  }

  private resolve(ids: string[]): Promise<AdGroup[]> {
    return this.request('/security-groups/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
  }

  private async request(path: string, init?: RequestInit): Promise<AdGroup[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('common-utils');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (!response.ok) {
      throw new Error(`Failed to resolve AD groups: ${response.status}`);
    }

    const { groups } = await response.json();
    return groups ?? [];
  }
}
